import { supabase } from './supabaseClient';

const EVENT_TABLE = 'ml_event_stream_staging';

function normalizeText(value) {
  const text = String(value || '').trim();
  return text || null;
}

function normalizeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeArray(value) {
  return Array.isArray(value) ? value.map((item) => String(item).trim()).filter(Boolean) : [];
}

function buildStreamKey(event) {
  return (
    normalizeText(event.streamKey) ||
    [
      normalizeText(event.eventFamily),
      normalizeText(event.origin),
      normalizeText(event.courseId),
      normalizeText(event.unitId),
      normalizeText(event.activityId),
      normalizeText(event.entityId),
    ]
      .filter(Boolean)
      .join(':') ||
    null
  );
}

function toInsertRow(event = {}) {
  const occurredAt = event.occurredAt || new Date().toISOString();
  const payload = event.payload && typeof event.payload === 'object' ? event.payload : {};
  const metrics = event.metrics && typeof event.metrics === 'object' ? event.metrics : {};

  return {
    occurred_at: occurredAt,
    user_id: normalizeText(event.userId),
    session_id: normalizeText(event.sessionId),
    correlation_id: normalizeText(event.correlationId),
    conversation_key: normalizeText(event.conversationKey),
    stream_key: buildStreamKey(event),
    event_family: normalizeText(event.eventFamily) || 'system',
    event_type: normalizeText(event.eventType) || 'unknown',
    event_name: normalizeText(event.eventName) || normalizeText(event.eventType) || 'unknown',
    origin: normalizeText(event.origin),
    actor_type: normalizeText(event.actorType) || 'user',
    actor_id: normalizeText(event.actorId),
    entity_type: normalizeText(event.entityType),
    entity_id: normalizeText(event.entityId),
    parent_entity_type: normalizeText(event.parentEntityType),
    parent_entity_id: normalizeText(event.parentEntityId),
    course_id: normalizeText(event.courseId),
    unit_id: normalizeText(event.unitId),
    activity_id: normalizeText(event.activityId),
    lesson_id: normalizeText(event.lessonId),
    exam_id: normalizeText(event.examId),
    reflection_id: normalizeText(event.reflectionId),
    task_id: normalizeText(event.taskId),
    scene_id: normalizeText(event.sceneId),
    message_role: normalizeText(event.messageRole),
    sequence_index: normalizeNumber(event.sequenceIndex),
    duration_ms: normalizeNumber(event.durationMs),
    score_raw: normalizeNumber(event.scoreRaw),
    score_max: normalizeNumber(event.scoreMax),
    score_pct: normalizeNumber(event.scorePct),
    tokens_in: normalizeNumber(event.tokensIn),
    tokens_out: normalizeNumber(event.tokensOut),
    success: event.success !== false,
    error_code: normalizeText(event.errorCode),
    labels: normalizeArray(event.labels),
    payload,
    metrics,
  };
}

const eventStreamService = {
  async emitEvent(event) {
    const row = toInsertRow(event);
    const { data, error } = await supabase.from(EVENT_TABLE).insert(row).select().single();
    return { data, error };
  },

  async emitBatch(events) {
    const rows = (Array.isArray(events) ? events : []).map((event) => toInsertRow(event));
    if (rows.length === 0) return { data: [], error: null };
    const { data, error } = await supabase.from(EVENT_TABLE).insert(rows).select();
    return { data: data || [], error };
  },

  subscribeToUserEvents({ userId, onEvent, eventFamily = null }) {
    if (!userId || typeof onEvent !== 'function') return null;

    const filters = [`user_id=eq.${userId}`];
    if (eventFamily) {
      filters.push(`event_family=eq.${eventFamily}`);
    }

    const channel = supabase
      .channel(`event-stream:${userId}:${eventFamily || 'all'}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: EVENT_TABLE,
          filter: filters.join(','),
        },
        (payload) => onEvent(payload.new)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  async fetchRecentEvents({ userId, eventFamily = null, limit = 50 }) {
    if (!userId) return { data: [], error: null };

    let query = supabase
      .from(EVENT_TABLE)
      .select('*')
      .eq('user_id', userId)
      .order('occurred_at', { ascending: false })
      .limit(Math.max(1, Math.min(200, Number(limit) || 50)));

    if (eventFamily) {
      query = query.eq('event_family', eventFamily);
    }

    const { data, error } = await query;
    return { data: data || [], error };
  },

  async fetchEvents({
    userId,
    eventFamily = null,
    courseId = null,
    unitId = null,
    lessonId = null,
    examId = null,
    sceneId = null,
    from = null,
    to = null,
    limit = 500,
  }) {
    if (!userId) return { data: [], error: null };

    let query = supabase
      .from(EVENT_TABLE)
      .select('*')
      .eq('user_id', userId)
      .order('occurred_at', { ascending: false })
      .limit(Math.max(1, Math.min(2000, Number(limit) || 500)));

    if (eventFamily) query = query.eq('event_family', eventFamily);
    if (courseId) query = query.eq('course_id', courseId);
    if (unitId) query = query.eq('unit_id', unitId);
    if (lessonId) query = query.eq('lesson_id', lessonId);
    if (examId) query = query.eq('exam_id', examId);
    if (sceneId) query = query.eq('scene_id', sceneId);
    if (from) query = query.gte('occurred_at', from);
    if (to) query = query.lte('occurred_at', to);

    const { data, error } = await query;
    return { data: data || [], error };
  },
};

export default eventStreamService;
